import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api.js';
import { MENU_ITEMS, routeFor } from '../../layouts/menuData.js';
import './connector-reservation.scss';

const BLUE = { color: '#005293' };
const AMBER = { color: '#d97706' };
const POLL_MS = 5000;
const DEFAULT_FEES = { 15: 30, 30: 50 };
const peso = (v) => `₱${Number(v ?? 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
const clock12 = (iso) => new Date(iso).toLocaleTimeString('en-PH', { hour: 'numeric', minute: '2-digit', hour12: true });
const PLUG_LABEL = { CCS2: 'CCS 2', Type2: 'Type 2', CHAdeMO: 'CHAdeMO', GBT: 'GB/T' };
const routeOf = (slug) => routeFor(MENU_ITEMS.find((m) => m.slug === slug) || { built: false });

/** Seconds left in the arrival window, anchored to the server clock so a skewed device clock cannot distort it. */
function useRemaining(res) {
  const [now, setNow] = useState(Date.now());
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    if (res?.serverTime) setOffset(new Date(res.serverTime).getTime() - Date.now());
  }, [res?.serverTime]);
  useEffect(() => {
    if (res?.status !== 'active') return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [res?.status]);
  if (!res || res.status !== 'active') return 0;
  return Math.max(0, Math.ceil((new Date(res.expiresAt).getTime() - (now + offset)) / 1000));
}

function ActiveReservation({ res, onCancel, onArrived, onReload, busy, error }) {
  const remaining = useRemaining(res);
  const active = res.status === 'active';
  useEffect(() => {
    if (active && remaining === 0) onReload();
  }, [active, remaining, onReload]);
  const outcome = {
    expired: ['Reservation Expired', 'The arrival window elapsed and the connector has been released to the fleet. The reservation fee was forfeited. A notice is listed under Notifications below.', '#ef4444'],
    cancelled: ['Reservation Cancelled', 'The connector lock was released. The reservation fee is non-refundable.', '#6b7280'],
    fulfilled: ['Reservation Fulfilled', 'You arrived in time and your charging session has begun.', '#10b981'],
  }[res.status];

  return (
    <div id="active-state-view" className="row justify-content-center">
      <div className="col-12 col-md-10 col-lg-8">
        <div className="card p-4 p-md-5 text-center shadow-lg border-warning bg-white" style={{ border: '2px solid #fecc00', borderRadius: 16 }}>
          <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3 mx-auto" style={{ width: 64, height: 64, background: '#fffbeb', border: '2px solid #fecc00' }}>
            <i className={`fa ${active ? 'fa-lock' : 'fa-lock-open'} fa-2x`} style={AMBER} aria-hidden="true" />
          </div>
          <h2 className="h3 fw-bold mb-1" style={BLUE} id="active-heading">{active ? 'Connector Locked Exclusively for You' : outcome[0]}</h2>
          <p className="text-muted mb-4">
            {active ? <>OCPP <code>ReserveNow</code> command confirmed. Other drivers cannot initiate sessions on this bay.</> : outcome[1]}
          </p>
          {error && <div className="alert alert-danger" role="alert" id="reservation-error">{error}</div>}

          <div className="p-4 rounded mb-4 mx-auto border" style={{ maxWidth: 380, background: '#f8fafc', borderRadius: 14 }}>
            <span className="text-muted small text-uppercase fw-bold d-block mb-1" style={{ letterSpacing: 1.5 }}>Remaining Window</span>
            <div className="display-3 fw-bold mb-1" style={{ color: active ? '#d97706' : outcome[2] }} id="timer-display" data-testid="timer-display">
              {active ? mmss(remaining) : res.status.toUpperCase()}
            </div>
            <small className="text-muted d-block">Expires at: <strong className="text-dark" id="timer-expiry-time">{clock12(res.expiresAt)}</strong></small>
          </div>

          <div className="row text-start mb-4 p-3 rounded bg-light border mx-auto" style={{ maxWidth: 650 }}>
            <div className="col-6 col-sm-3 mb-2 mb-sm-0"><span className="text-muted small d-block">Reservation Code</span><span className="fw-bold text-dark" id="active-res-code">{res.reservationCode}</span></div>
            <div className="col-6 col-sm-3 mb-2 mb-sm-0"><span className="text-muted small d-block">OCPP ID</span><span className="fw-bold" style={BLUE} id="active-ocpp-id">#{res.ocppReservationId}</span></div>
            <div className="col-6 col-sm-3"><span className="text-muted small d-block">Plaza</span><span className="fw-bold text-dark" id="active-plaza-name">{res.station.siteName}</span></div>
            <div className="col-6 col-sm-3"><span className="text-muted small d-block">Assigned Bay</span><span className="fw-bold text-success" id="active-bay">{res.station.chargePointCode} #{res.station.connectorIndex} ({PLUG_LABEL[res.station.connectorType] || res.station.connectorType})</span></div>
          </div>

          {active && res.station.latitude != null && (
            <p className="small mb-4">
              <a id="link-directions" href={`https://www.google.com/maps/dir/?api=1&destination=${res.station.latitude},${res.station.longitude}`} target="_blank" rel="noreferrer" style={BLUE}>
                <i className="fa fa-diamond-turn-right me-1" aria-hidden="true" />Get directions to {res.station.siteName}
              </a>
              <span className="text-muted"> &bull; {res.station.address}</span>
            </p>
          )}

          <div className="d-flex flex-column flex-sm-row justify-content-center">
            {active ? (
              <>
                <button type="button" id="btn-arrived-charge" className="btn btn-primary fw-bold py-3 px-4 me-0 me-sm-3 mb-2 mb-sm-0" disabled={busy} onClick={onArrived}>
                  <i className="fa fa-plug-circle-bolt me-2" aria-hidden="true" /> I Have Arrived &mdash; Start Charging
                </button>
                <button type="button" id="btn-cancel-reservation" className="btn btn-outline-danger fw-bold py-3 px-4" disabled={busy} onClick={onCancel}>
                  <i className="fa fa-times-circle me-2" aria-hidden="true" /> Cancel Reservation
                </button>
              </>
            ) : (
              <>
                <button type="button" id="btn-book-another" className="btn btn-warning fw-bold py-3 px-4 me-0 me-sm-3 mb-2 mb-sm-0" onClick={onReload.bind(null, true)}>Reserve another connector</button>
                {res.status === 'fulfilled' && <Link to={routeOf('live-charging-telemetry')} className="btn btn-primary fw-bold py-3 px-4">View charging session</Link>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The driver's reservation notices (confirmed, expiring, expired, refunded), newest first. */
function ReservationNotifications({ refreshKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let live = true;
    api.listReservationNotifications().then((list) => live && setItems(Array.isArray(list) ? list : [])).catch(() => live && setItems([]));
    return () => { live = false; };
  }, [refreshKey]);
  if (!items.length) return null;
  return (
    <div className="card p-3 mt-4 shadow-sm border bg-white" id="reservation-notifications">
      <h5 className="fw-bold mb-2" style={{ color: '#005293' }}><i className="fa fa-bell me-2" aria-hidden="true" />Notifications</h5>
      <p className="text-muted small mb-2">Notices about your reservations appear here in the app.</p>
      {items.slice(0, 8).map((n) => (
        <div key={n.id} className="py-2 border-top" style={{ overflowWrap: 'anywhere' }}>
          <div className="fw-semibold small">{n.title}</div>
          <div className="text-muted small">{n.message}</div>
          <div className="text-muted" style={{ fontSize: '0.72rem' }}>{new Date(n.createdAt).toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
}

export default function ConnectorReservationPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const siteParam = params.get('site_id');
  const [station, setStation] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [duration, setDuration] = useState(15);
  const [connectorId, setConnectorId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reload = useCallback(async (dismiss) => {
    if (dismiss === true) setDismissed(true);
    try {
      setRes(await api.getActiveReservation());
      setWallet(await api.getWallet());
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [active, w] = await Promise.all([api.getActiveReservation(), api.getWallet()]);
        let site = null;
        if (siteParam) site = await api.getStation(siteParam);
        else {
          const list = await api.listStations('pageSize=50');
          const withFree = (Array.isArray(list) ? list : list?.items || []).find((s) => s.connectors.some((c) => c.status === 'Available'));
          site = withFree || (Array.isArray(list) ? list[0] : null) || null;
        }
        if (!live) return;
        setRes(active);
        setWallet(w);
        setStation(site);
        const first = site?.connectors.find((c) => c.status === 'Available');
        if (first) setConnectorId(first.id);
      } catch (e) {
        if (live) setError(e.message);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [siteParam]);

  // Keep an open reservation in sync with the server (expiry sweep, fulfilment from another tab).
  useEffect(() => {
    if (res?.status !== 'active') return undefined;
    const t = setInterval(() => reload(), POLL_MS);
    return () => clearInterval(t);
  }, [res?.status, reload]);

  const fees = { 15: station?.tariff?.reservationFee15 ?? DEFAULT_FEES[15], 30: station?.tariff?.reservationFee30 ?? DEFAULT_FEES[30] };
  const fee = fees[duration];
  const available = wallet ? wallet.balance - wallet.heldBalance : 0;
  const afterBalance = (wallet?.balance ?? 0) - fee;
  const insufficient = wallet && available < fee;

  const confirm = async () => {
    setError('');
    if (!connectorId) {
      setError('Select an available connector first.');
      return;
    }
    setBusy(true);
    try {
      const r = await api.createReservation({ connectorId, durationMinutes: duration });
      setDismissed(false);
      setRes(r);
      setWallet(await api.getWallet());
    } catch (e) {
      setError(e.message);
      if (siteParam || station) api.getStation(station.id).then(setStation).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this reservation? The connector lock will be released and the fee is non-refundable.')) return;
    setBusy(true);
    setError('');
    try {
      setRes(await api.cancelReservation(res.id));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const arrived = async () => {
    setBusy(true);
    setError('');
    try {
      await api.startSession({ connectorId: res.station.connectorId });
      navigate(routeOf('live-charging-telemetry'));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="d-flex justify-content-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div></div>;

  const showActive = res && (res.status === 'active' || !dismissed);
  const connectors = station?.connectors || [];
  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col-12">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb bg-transparent p-0 mb-2">
              <li className="breadcrumb-item"><Link to={routeOf('station-locator-availability')} style={BLUE}><i className="fa fa-charging-station me-1" />Station Locator</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Connector Reservation</li>
            </ol>
          </nav>
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <h1 className="h3 fw-bold mb-1" style={BLUE}><i className="fa fa-clock me-2" style={AMBER} aria-hidden="true" />Guaranteed Connector Reservation</h1>
              <p className="text-muted small mb-0">Lock a high-speed charging connector in advance. Enjoy guaranteed bay availability upon arrival.</p>
            </div>
            <div className="mt-2 mt-md-0">
              <span className="badge py-2 px-3" style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' }}><i className="fa fa-bolt me-1" />OCPP 1.6-J / 2.0.1 ReserveNow Protocol</span>
            </div>
          </div>
        </div>
      </div>

      {showActive ? (
        <ActiveReservation res={res} onCancel={cancel} onArrived={arrived} onReload={reload} busy={busy} error={error} />
      ) : (
        <div id="booking-state-view" className="row">
          <div className="col-12 col-lg-7 mb-4">
            <div className="card p-4 mb-4 shadow-sm border bg-white">
              <h5 className="fw-bold mb-3" style={BLUE}><i className="fa fa-location-dot text-danger me-2" />Target Charging Plaza</h5>
              {station ? (
                <div className="d-flex align-items-center justify-content-between p-3 rounded mb-3 bg-light border">
                  <div>
                    <h6 className="fw-bold mb-1" style={BLUE} id="res-site-name">{station.name}</h6>
                    <p className="text-muted small mb-0" id="res-site-address"><i className="fa fa-map-pin me-1" />{station.address}{station.city ? `, ${station.city}` : ''}</p>
                  </div>
                  <span className={`badge ${station.status === 'operational' ? 'badge-available' : 'badge-faulted'}`}>{station.status === 'operational' ? 'Online & Active' : 'Under Maintenance'}</span>
                </div>
              ) : (
                <div className="alert alert-warning">No station selected. Open one from <Link to={routeOf('station-locator-availability')}>Find Stations</Link>.</div>
              )}
              <label className="form-label small fw-bold mb-2">Select Available Bay &amp; Plug</label>
              <div className="row">
                {connectors.map((c) => {
                  const ok = c.status === 'Available';
                  return (
                    <div className="col-12 col-md-6 mb-2" key={c.id}>
                      <div role="button" tabIndex={ok ? 0 : -1} data-testid="connector-option" aria-disabled={!ok}
                        className={`connector-option-card p-3 rounded ${connectorId === c.id ? 'active' : ''}`} style={ok ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                        onClick={() => ok && setConnectorId(c.id)} onKeyDown={(e) => ok && (e.key === 'Enter' || e.key === ' ') && setConnectorId(c.id)}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="fw-bold text-dark">{c.chargePoint} #{c.index} - {PLUG_LABEL[c.type] || c.type}</span>
                          <span className={`badge small ${ok ? 'badge-available' : c.status === 'Reserved' ? 'badge-reserved' : 'badge-faulted'}`}>{ok ? 'Available' : c.status}</span>
                        </div>
                        <p className="text-muted small mb-0"><i className="fa fa-bolt text-warning me-1" />{c.maxPowerKw} kW</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card p-4 shadow-sm border bg-white">
              <h5 className="fw-bold mb-2" style={BLUE}><i className="fa fa-stopwatch me-2 text-primary" />Select Holding Duration</h5>
              <p className="text-muted small mb-3">Choose how long ChargeOS should lock the selected connector exclusively for your vehicle:</p>
              <div className="row mb-3">
                {[15, 30].map((d) => (
                  <div className={`col-12 col-sm-6 ${d === 15 ? 'mb-3 mb-sm-0' : ''}`} key={d}>
                    <div role="button" tabIndex={0} id={`duration-card-${d}`} data-duration={d} data-fee={fees[d]}
                      className={`duration-card p-3 rounded text-center ${duration === d ? 'active' : ''}`}
                      onClick={() => setDuration(d)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setDuration(d)}>
                      <div className={`badge ${d === 15 ? 'badge-primary' : 'badge-warning'} mb-2`}>{d === 15 ? 'Quick Arrival' : 'Extended Window'}</div>
                      <h3 className="fw-bold mb-1" style={BLUE}>{d} Minutes</h3>
                      <p className="text-muted small mb-2">{d === 15 ? 'Ideal if you are 5–10 minutes away' : 'Ideal for navigating city traffic'}</p>
                      <div className="h4 fw-bold mb-0" style={AMBER}>{peso(fees[d])} <span className="small text-muted fw-normal">PHP</span></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="alert alert-secondary p-2 mb-0 small text-muted bg-light border">
                <i className="fa fa-info-circle me-1 text-primary" />The reservation holds this connector for you: other drivers cannot start a charge on it from the app until you arrive or the window ends.
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-5">
            <div className="card p-4 shadow-sm border bg-white">
              <h5 className="fw-bold mb-3" style={BLUE}><i className="fa fa-wallet text-success me-2" />Prepaid Wallet Payment</h5>
              <div className="p-3 rounded mb-3 bg-light border">
                <div className="d-flex justify-content-between align-items-center mb-2"><span className="text-muted small">Current Wallet Balance</span><span className="fw-bold text-dark" id="user-wallet-balance">{peso(wallet?.balance)} PHP</span></div>
                <div className="d-flex justify-content-between align-items-center mb-2"><span className="text-muted small">Reservation Fee (<span id="summary-duration">{duration}</span> min)</span><span className="fw-bold text-danger" id="summary-fee">- {peso(fee)} PHP</span></div>
                <hr className="my-2" />
                <div className="d-flex justify-content-between align-items-center"><span className="text-dark small fw-bold">Balance After Booking</span><span className={`fw-bold ${insufficient ? 'text-danger' : 'text-success'}`} id="summary-after-balance">{peso(afterBalance)} PHP</span></div>
              </div>
              {insufficient && <div className="alert alert-warning small" role="alert" id="insufficient-funds">Your available balance is too low for this reservation. <Link to={routeOf('wallet-top-up-payment')}>Top up your wallet</Link>.</div>}
              <div className="mb-4 small text-muted">
                <h6 className="fw-bold small mb-1 text-dark">Reservation Policy:</h6>
                <ul className="ps-3 mb-0">
                  <li>Connector switches to <strong>Reserved</strong> immediately upon booking.</li>
                  <li>If you do not start charging before expiry, the fee is non-refundable.</li>
                  <li>Charging sessions initiated within the window automatically fulfill the hold.</li>
                </ul>
              </div>
              {error && <div className="alert alert-danger small" role="alert" id="reservation-error">{error}</div>}
              <button type="button" id="btn-confirm-reserve" className="btn btn-warning w-100 fw-bold py-3 mb-3" disabled={busy || !connectorId || !station} onClick={confirm}>
                {busy && <span id="reserve-spinner" className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
                <span id="reserve-text">{busy ? 'Dispatching OCPP ReserveNow...' : <><i className="fa fa-lock me-2" aria-hidden="true" />Confirm &amp; Lock Connector</>}</span>
              </button>
              <Link to={routeOf('station-locator-availability')} className="btn btn-outline-secondary w-100 small" id="btn-back-to-locator">
                <i className="fa fa-arrow-left me-1" aria-hidden="true" /> Back to Station Locator
              </Link>
            </div>
          </div>
        </div>
      )}
      <ReservationNotifications refreshKey={res?.status || 'none'} />
    </div>
  );
}
